// src/components/forms/LoginForm.jsx

import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, TextField, Button, Typography, Alert, InputAdornment, IconButton, CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, Person, Lock } from '@mui/icons-material';
import { useLoginMutation } from '../../../store/api/authApi';
import { loginSchema, loginDefaultValues } from './validation';
import { ROUTES } from '../../../utils/constants';

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pendingNotice = location.state?.notice;
  const [showPassword, setShowPassword] = React.useState(false);

  // Get loading and error state DIRECTLY from the mutation hook.
  const [login, { isLoading, error: apiError, isSuccess }] = useLoginMutation();

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors },
  } = useForm({
    resolver: yupResolver(loginSchema),
    defaultValues: loginDefaultValues,
  });

  // On successful login (isSuccess becomes true), navigate to dashboard.
  useEffect(() => {
    if (isSuccess) {
      navigate(ROUTES.DASHBOARD);
    }
  }, [isSuccess, navigate]);

  // The onSubmit function now only needs to trigger the mutation.
  const onSubmit = (data) => {
    login(data);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 3 }}>
        Sign In
      </Typography>

      {/* Display the error message from the hook's error object. It will persist. */}
      {pendingNotice && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {pendingNotice}
        </Alert>
      )}
      {apiError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiError.data?.message || apiError.data?.error || 'Login failed. Please check credentials.'}
        </Alert>
      )}

      <Controller
        name="identifier"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label="Email or username"
            placeholder="you@example.com or your_username"
            error={!!formErrors.identifier}
            helperText={formErrors.identifier?.message}
            margin="normal"
            autoComplete="username"
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><Person color="action" /></InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
        )}
      />
      <Controller
        name="password"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            error={!!formErrors.password}
            helperText={formErrors.password?.message}
            margin="normal"
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><Lock color="action" /></InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />
        )}
      />

      {/* The button is disabled by the hook's `isLoading` state. */}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        disabled={isLoading}
        sx={{ py: 1.5, mb: 2 }}
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
      </Button>

    </Box>
  );
};

export default LoginForm;

// import React, { useState } from 'react';
// import { useForm, Controller } from 'react-hook-form';
// import { yupResolver } from '@hookform/resolvers/yup';
// import { useSelector } from 'react-redux';
// import { useNavigate, Link } from 'react-router-dom';
// import {
//   Box,
//   TextField,
//   Button,
//   Typography,
//   Alert,
//   InputAdornment,
//   IconButton,
//   CircularProgress,
// } from '@mui/material';
// // FIX: Changed Person icon to Email icon for better context.
// import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
// import { useLoginMutation } from '../../../store/api/authApi';
// import { loginSchema, loginDefaultValues } from './validation';
// import { ROUTES } from '../../../utils/constants';

// const LoginForm = () => {
//   const navigate = useNavigate();
//   const { error } = useSelector((state) => state.auth);
//   const [showPassword, setShowPassword] = useState(false);

//   const [login, { isLoading }] = useLoginMutation();

//   const {
//     control,
//     handleSubmit,
//     formState: { errors },
//   } = useForm({
//     resolver: yupResolver(loginSchema),
//     defaultValues: loginDefaultValues,
//   });

//   const onSubmit = async (data) => {
//     try {
//       // The 'data' object will now be { email: '...', password: '...' }
//       await login(data).unwrap();
//       navigate(ROUTES.DASHBOARD);
//     } catch (err) {
//       console.error('Login failed:', err);
//     }
//   };

//   return (
//     <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
//       <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 3 }}>
//         Sign In
//       </Typography>

//       {error && (
//         <Alert severity="error" sx={{ mb: 2 }}>
//           {error}
//         </Alert>
//       )}

//       {/* FIX: Updated Controller to use 'email' instead of 'identifier' */}
//       <Controller
//         name="email"
//         control={control}
//         render={({ field }) => (
//           <TextField
//             {...field}
//             fullWidth
//             label="Email Address" // Updated label
//             error={!!errors.email}
//             helperText={errors.email?.message}
//             margin="normal"
//             autoComplete="email" // Updated autocomplete
//             autoFocus
//             InputProps={{
//               startAdornment: (
//                 <InputAdornment position="start">
//                   <Email color="action" />
//                 </InputAdornment>
//               ),
//             }}
//             sx={{ mb: 2 }}
//           />
//         )}
//       />

//       <Controller
//         name="password"
//         control={control}
//         render={({ field }) => (
//           <TextField
//             {...field}
//             fullWidth
//             label="Password"
//             type={showPassword ? 'text' : 'password'}
//             error={!!errors.password}
//             helperText={errors.password?.message}
//             margin="normal"
//             autoComplete="current-password"
//             InputProps={{
//               startAdornment: (
//                 <InputAdornment position="start">
//                   <Lock color="action" />
//                 </InputAdornment>
//               ),
//               endAdornment: (
//                 <InputAdornment position="end">
//                   <IconButton
//                     aria-label="toggle password visibility"
//                     onClick={() => setShowPassword(!showPassword)}
//                     edge="end"
//                   >
//                     {showPassword ? <VisibilityOff /> : <Visibility />}
//                   </IconButton>
//                 </InputAdornment>
//               ),
//             }}
//             sx={{ mb: 3 }}
//           />
//         )}
//       />

//       <Button
//         type="submit"
//         fullWidth
//         variant="contained"
//         color="primary"
//         disabled={isLoading}
//         sx={{
//           py: 1.5,
//           mb: 2,
//           bgcolor: 'primary.main',
//           '&:hover': {
//             bgcolor: 'primary.dark',
//           },
//         }}
//       >
//         {isLoading ? (
//           <CircularProgress size={24} color="inherit" />
//         ) : (
//           'Sign In'
//         )}
//       </Button>

//       <Box textAlign="center">
//         <Typography variant="body2" color="text.secondary">
//           Don't have an account?{' '}
//           <Link
//             to={ROUTES.REGISTER}
//             style={{
//               color: '#3fbb85',
//               textDecoration: 'none',
//               fontWeight: 500,
//             }}
//           >
//             Sign up here
//           </Link>
//         </Typography>
//       </Box>
//     </Box>
//   );
// };

// export default LoginForm;


// // src/components/forms/LoginForm/LoginForm.jsx
// import React, { useState } from 'react';
// import { useForm, Controller } from 'react-hook-form';
// import { yupResolver } from '@hookform/resolvers/yup';
// import { useDispatch, useSelector } from 'react-redux';
// import { useNavigate, Link } from 'react-router-dom';
// import {
//   Box,
//   TextField,
//   Button,
//   Typography,
//   Alert,
//   InputAdornment,
//   IconButton,
//   CircularProgress,
// } from '@mui/material';
// import { Visibility, VisibilityOff, Person, Lock } from '@mui/icons-material';
// import { useLoginMutation } from '../../../store/api/authApi';
// import { loginSchema, loginDefaultValues } from './validation';
// import { ROUTES } from '../../../utils/constants';

// const LoginForm = () => {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const { error } = useSelector((state) => state.auth);
//   const [showPassword, setShowPassword] = useState(false);
  
//   const [login, { isLoading }] = useLoginMutation();

//   const {
//     control,
//     handleSubmit,
//     formState: { errors },
//   } = useForm({
//     resolver: yupResolver(loginSchema),
//     defaultValues: loginDefaultValues,
//   });

//   const onSubmit = async (data) => {
//     try {
//       await login(data).unwrap();
//       navigate(ROUTES.DASHBOARD);
//     } catch (err) {
//       // Error handled by RTK Query and stored in Redux
//       console.error('Login failed:', err);
//     }
//   };

//   const handleTogglePassword = () => {
//     setShowPassword(!showPassword);
//   };

//   return (
//     <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
//       <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 3 }}>
//         Sign In
//       </Typography>

//       {error && (
//         <Alert severity="error" sx={{ mb: 2 }}>
//           {error}
//         </Alert>
//       )}

//       <Controller
//         name="identifier"
//         control={control}
//         render={({ field }) => (
//           <TextField
//             {...field}
//             fullWidth
//             label="Username or Email"
//             error={!!errors.identifier}
//             helperText={errors.identifier?.message}
//             margin="normal"
//             autoComplete="username"
//             autoFocus
//             InputProps={{
//               startAdornment: (
//                 <InputAdornment position="start">
//                   <Person color="action" />
//                 </InputAdornment>
//               ),
//             }}
//             sx={{ mb: 2 }}
//           />
//         )}
//       />

//       <Controller
//         name="password"
//         control={control}
//         render={({ field }) => (
//           <TextField
//             {...field}
//             fullWidth
//             label="Password"
//             type={showPassword ? 'text' : 'password'}
//             error={!!errors.password}
//             helperText={errors.password?.message}
//             margin="normal"
//             autoComplete="current-password"
//             InputProps={{
//               startAdornment: (
//                 <InputAdornment position="start">
//                   <Lock color="action" />
//                 </InputAdornment>
//               ),
//               endAdornment: (
//                 <InputAdornment position="end">
//                   <IconButton
//                     aria-label="toggle password visibility"
//                     onClick={handleTogglePassword}
//                     edge="end"
//                   >
//                     {showPassword ? <VisibilityOff /> : <Visibility />}
//                   </IconButton>
//                 </InputAdornment>
//               ),
//             }}
//             sx={{ mb: 3 }}
//           />
//         )}
//       />

//       <Button
//         type="submit"
//         fullWidth
//         variant="contained"
//         color="primary"
//         disabled={isLoading}
//         sx={{
//           py: 1.5,
//           mb: 2,
//           bgcolor: 'primary.main',
//           '&:hover': {
//             bgcolor: 'primary.dark',
//           },
//         }}
//       >
//         {isLoading ? (
//           <CircularProgress size={24} color="inherit" />
//         ) : (
//           'Sign In'
//         )}
//       </Button>

//       <Box textAlign="center">
//         <Typography variant="body2" color="text.secondary">
//           Don't have an account?{' '}
//           <Link 
//             to={ROUTES.REGISTER}
//             style={{ 
//               color: '#3fbb85', 
//               textDecoration: 'none',
//               fontWeight: 500,
//             }}
//           >
//             Sign up here
//           </Link>
//         </Typography>
//       </Box>
//     </Box>
//   );
// };

// export default LoginForm;


